import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiPaperclip,
  FiMic,
  FiArrowUp,
  FiHome,
  FiHeadphones,
  FiSmartphone,
  FiMail,
  FiMapPin,
  FiGlobe,
  FiCheck,
  FiLink,
  FiRotateCcw,
  FiHelpCircle,
} from "react-icons/fi";
import {
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram,
  FaYoutube,
  FaGithub,
  FaTiktok,
  FaSnapchatGhost,
  FaPinterestP,
  FaWhatsapp,
  FaTelegramPlane,
  FaSpotify,
  FaVimeoV,
  FaDribbble,
  FaSoundcloud,
  FaGoogle,
  FaFacebookMessenger,
  FaSkype,
  FaDropbox,
  FaWordpress,
  FaBehance,
  FaTumblr,
} from "react-icons/fa";
import { GrAndroid } from "react-icons/gr";
import theme from "../assets/images/color-wheel1.png";
import aiLogo from "../assets/images/ai-logo.png";
import { GiPencilBrush } from "react-icons/gi";
import baseURL from "../utils/constants/baseUrl";
import { CardBackGroundImages } from "../utils/data/CardFunctionsData";
import showToast from "../helpers/showToast";
import { AI_CARD_SESSIONS } from "../utils/constants/apiUrl";
import cardSuccessIcon from "../assets/images/function_page_icons/cardsuccess.svg";
import { useAtom } from "jotai";
import { loggedInUser, subscribedPackage, userIDCheck, teamIDCheck } from "../utils/store";
import formatPhoneNumber from "../helpers/formatPhoneNumber";
import { IoPlaySkipForwardOutline } from "react-icons/io5";
import { IoMdArrowBack } from "react-icons/io";
import useFunctionsCount from "../hooks/useFunctionsCount";
import functionTypes from "../utils/constants/functionTypes";
import { get, post } from "../server";
import { GET_USER_SUBSCRIPTION } from "../utils/constants/apiUrl";
import FeatureNotAvailableModal from "../components/Modals/FeatureNotAvailableModal";

const EDITABLE_FIELDS = [
  { label: "Full Name", value: "name" },
  { label: "First Name", value: "first_name" },
  { label: "Last Name", value: "last_name" },
  { label: "Job Title", value: "job_title" },
  { label: "Company", value: "company_name" },
  { label: "Email", value: "email_address" },
  { label: "Phone", value: "mobile_number" },
  { label: "Website", value: "website_url" },
  { label: "Location", value: "address" },
  { label: "Description", value: "description" },
  { label: "Pronouns", value: "pronouns" },
];

const SOCIAL_BASE_URLS = {
  Facebook: "https://www.facebook.com/",
  Twitter: "https://x.com/",
  Instagram: "https://www.instagram.com/",
  LinkedIn: "https://www.linkedin.com/in/",
  YouTube: "https://www.youtube.com/",
  Spotify: "https://open.spotify.com/user/",
  Vimeo: "https://vimeo.com/",
  Dribble: "https://dribbble.com/",
  SoundCloud: "https://soundcloud.com/",
  Pinterest: "https://www.pinterest.com/",
  Android: "https://play.google.com/store/apps/details?id=com.",
};

const AiCardChatPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loggedUser] = useAtom(loggedInUser);
  const [purchasedPackage, setPurchasedPackage] = useAtom(subscribedPackage);
  const [userID] = useAtom(userIDCheck);
  const [userTeamId] = useAtom(teamIDCheck);
  const { compareLimitsForDynamicQR } = useFunctionsCount();

  const [sessionUuid, setSessionUuid] = useState(
    location?.state?.session_uuid || null
  );
  const [showLimitModal, setShowLimitModal] = useState(false);
  const sessionUuidRef = useRef(null);

  useEffect(() => {
    sessionUuidRef.current = sessionUuid;
  }, [sessionUuid]);

  const pageUserName = location?.state?.pageUserName || "";
  const hasName = Boolean(pageUserName?.trim());

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const team_id = searchParams.get("team_id");
  const user_id = searchParams.get("user_id");
  const dynamicQRCodeId = searchParams.get("dynamic_qr_code");

  const [message, setMessage] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  const isRestricted = useMemo(() => {
    return dynamicQRCodeId !== null
      ? compareLimitsForDynamicQR(functionTypes.CARD, dynamicQRCodeId)
      : Number(purchasedPackage?.id || 0) === 1;
  }, [dynamicQRCodeId, purchasedPackage, compareLimitsForDynamicQR]);

  const getSubscriptionPackageDetails = async () => {
    const payload = {
      user_id: userID,
      team_id: userTeamId,
    };
    let { status, data } = await post(
      GET_USER_SUBSCRIPTION,
      payload,
      Object.keys(loggedUser).length !== 0 ? loggedUser.token : null
    );

    if (status && data?.[0]?.package) {
      setPurchasedPackage(data[0]?.package);
    }
  };

  useEffect(() => {
    getSubscriptionPackageDetails();
  }, []);

  // ✅ Store card data for preview
  const [cardData, setCardData] = useState({
    name: pageUserName || "",
    card_type: "Personal",
  });

  // update name if prop changes
  useEffect(() => {
    if (pageUserName) {
      setCardData((prev) => ({ ...prev, name: pageUserName }));
    }
  }, [pageUserName]);

  const [sessionLoading, setSessionLoading] = useState(false);
  const [chat, setChat] = useState([]);
  const [sending, setSending] = useState(false);

  const buildMessagesUrl = (uuid) => `${AI_CARD_SESSIONS}/${uuid}/messages`;

  const formatTime = (date) => {
    return new Date(date || Date.now()).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ✅ helper: append bot messages from response shape
  const appendBotMessagesFromResponse = (data) => {
    let botMessages = data?.data?.messages || [];
    const now = new Date();

    if (!botMessages.length && (data?.data?.message || data?.message)) {
      const one = data?.data?.message || data?.message;
      botMessages = [{ text: one }];
    }

    if (botMessages.length) {
      const topLevelUI = data?.data?.ui_elements || data?.ui_elements;

      setChat((prev) => [
        ...prev,
        ...botMessages
          .map((m, idx) => {
            const isLast = idx === botMessages.length - 1;
            const ui = isLast ? topLevelUI : null;

            const rawText = m?.text || "";
            let displayRefText = rawText;
            let highlightInput = false;
            let messageDelay = m?.delay ?? 0;

            if (typeof rawText === "object" && rawText !== null) {
              displayRefText = rawText.text || "";
              highlightInput = !!rawText.highlight_input;
              if (rawText.delay !== undefined) messageDelay = rawText.delay;
            }

            return {
              from: "bot",
              text: displayRefText || "",
              ui: ui,
              delay: messageDelay,
              timestamp: now,
              isSuccessImage:
                isLast && data?.data?.session_status === "completed",
              isInstruction:
                isLast &&
                (highlightInput ||
                  displayRefText?.toLowerCase()?.includes("type your answer")),
            };
          })
          .filter((m) => m.text),
      ]);
    }

    if (data?.data?.session_status === "completed") {
      setTimeout(() => {
        navigate("/function/cards");
      }, 5000);
    }
    
    // Update skip state - prioritize ui_elements.can_skip for the current field
    const topUI = data?.data?.ui_elements || data?.ui_elements;
    const canSkipNow = 
      topUI?.can_skip ?? 
      data?.data?.can_skip ?? 
      data?.can_skip ?? 
      false;
    
    setCanSkip(canSkipNow);
  };

  // Theme Picker State
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [tempColor, setTempColor] = useState("");
  const [undoSpinning, setUndoSpinning] = useState(false);
  const [stepCount, setStepCount] = useState(0);
  const [canSkip, setCanSkip] = useState(false);

  // ✅ Visual editing
  const [editingField, setEditingField] = useState(null);
  const [isVisualEditMode, setIsVisualEditMode] = useState(false);

  const [isListening, setIsListening] = useState(false);
  const isListeningRef = useRef(false);
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);
  const chatEndRef = useRef(null);

  // ✅ IMPORTANT CHANGE:
  // visual-selected should ONLY apply when Visual Edit mode is ON
  const vSelected = (isActive) =>
    isVisualEditMode && isActive ? "visual-selected" : "";

  // Auto-scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat, sending]);

  const presetColors = [
    "#219653",
    "#eb5757",
    "#d97706",
    "#7000B5",
    "#f4acca",
    "#d4e157",
    "#2ecc71",
    "#2f54eb",
    "#ff4d88",
  ];

  const handleThemeClick = () => {
    setTempColor(cardData.color || "#7000B5");
    setShowThemePicker(!showThemePicker);
  };

  const handleConfirmTheme = async () => {
    setCardData((prev) => ({ ...prev, color: tempColor }));
    setShowThemePicker(false);

    if (!sessionUuid) return;

    try {
      const res = await fetch(
        `${AI_CARD_SESSIONS}/${sessionUuid}/theme/color`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(loggedUser?.token
              ? { Authorization: `Bearer ${loggedUser.token}` }
              : {}),
          },
          body: JSON.stringify({ color: tempColor }),
        }
      );

      const data = await res.json();
    if (!res.ok) throw new Error(data?.message || "Failed to update theme");

    // Sync with messages API to advance chat flow
    try {
      const msgRes = await fetch(`${AI_CARD_SESSIONS}/${sessionUuid}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(loggedUser?.token
            ? { Authorization: `Bearer ${loggedUser.token}` }
            : {}),
        },
        body: JSON.stringify({
          message: tempColor,
          selection: tempColor,
        }),
      });
      const msgData = await msgRes.json();
      if (msgRes.ok) {
        setChat((prev) => [
          ...prev,
          { from: "user", text: "Theme color updated" },
        ]);
        appendBotMessagesFromResponse(msgData);
      }
    } catch (msgErr) {
      console.error("Chat sync failed:", msgErr);
    }

    fetchPreview(sessionUuid);
    } catch (err) {
      showToast(false, err.message || "Theme update failed");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (fileInputRef.current) fileInputRef.current.value = "";

    const validTypes = ["image/jpeg", "image/png"];
    if (!validTypes.includes(file.type)) {
      showToast(false, "Only JPG/JPEG and PNG files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast(false, "File size must be less than 5MB");
      return;
    }

    if (!sessionUuid) {
      showToast(false, "Session not ready yet");
      return;
    }

    try {
      const currentBotMsg = [...chat]
        .reverse()
        .find((m) => m.from === "bot" && (m.ui?.field || m.ui_elements?.field));

      const currentField =
        currentBotMsg?.ui?.field ||
        currentBotMsg?.ui_elements?.field ||
        "profile_image";

      const uploadField = ["logo", "background_image"].includes(currentField)
        ? currentField
        : "profile_image";

      const formData = new FormData();
      formData.append("image", file);
      formData.append("field_name", uploadField);

      const uploadUrl = `${AI_CARD_SESSIONS}/${sessionUuid}/upload`;

      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          ...(loggedUser?.token
            ? { Authorization: `Bearer ${loggedUser.token}` }
            : {}),
        },
        body: formData,
      });

      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        throw new Error(
          `Upload failed (${res.status}): ${text || "Non-JSON error"}`
        );
      }

      if (!res.ok) {
        throw new Error(data?.message || `Upload failed (${res.status})`);
      }

      const imageUrl = data?.data?.url || data?.data?.image_url || data?.url;
      if (!imageUrl) throw new Error("Upload ok but URL missing");

      setCardData((prev) => ({ ...prev, [uploadField]: imageUrl }));

      setChat((prev) => [
        ...prev,
        {
          from: "user",
          text: `Uploaded ${uploadField.replace("_", " ")}`,
          image: imageUrl,
        },
      ]);
      setStepCount((prev) => prev + 1);

      const agent = data?.data?.agent_response || data?.agent_response;
      if (isVisualEditMode) {
        setChat((prev) => [
          ...prev,
          {
            from: "bot",
            text: `${uploadField
              .replace("_", " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())} updated successfully.`,
          },
        ]);
      } else if (agent) {
        appendBotMessagesFromResponse({ data: agent });
      }

      fetchPreview(sessionUuid);
    } catch (err) {
      showToast(false, err?.message || "Upload failed");
    }
  };

  // ✅ PREVIEW API
  const fetchPreview = async (uuid) => {
    if (!uuid) return;
    try {
      const res = await fetch(`${AI_CARD_SESSIONS}/${uuid}/preview`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(loggedUser?.token
            ? { Authorization: `Bearer ${loggedUser.token}` }
            : {}),
        },
      });
      const data = await res.json();
      if (res.ok && data?.data) {
        const incomingData = data.data.card_data || data.data;

        Object.keys(incomingData).forEach((key) => {
          if (incomingData[key] === "-") {
            incomingData[key] = "";
          }
        });

        setCardData({
          name: pageUserName || "",
          card_type: "Personal",
          ...incomingData,
        });
      }
    } catch (err) {
      console.error("Preview fetch failed", err);
    }
  };

  // ✅ 1) create/resume session
  useEffect(() => {
    if (sessionUuid) return;

    const createSession = async () => {
      try {
        setSessionLoading(true);

        const payload = {
          name: pageUserName || null,
          team_id: team_id || null,
          user_id: user_id || null,
          dynamic_qr_code: dynamicQRCodeId || null,
          platform: "web",
        };

        const res = await fetch(AI_CARD_SESSIONS, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(loggedUser?.token
              ? { Authorization: `Bearer ${loggedUser.token}` }
              : {}),
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data?.message || "Failed to create session");
        }

        const uuid = data?.data?.session_uuid || null;
        if (!uuid) throw new Error("session_uuid missing in response");

        setSessionUuid(uuid);
      } catch (err) {
        showToast(false, err.message || "Session create failed");
      } finally {
        setSessionLoading(false);
      }
    };

    createSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionUuid, pageUserName, team_id, user_id, dynamicQRCodeId, loggedUser?.token]);

  useEffect(() => {
    const handleUnload = () => {
      if (sessionUuidRef.current) {
        const url = `${AI_CARD_SESSIONS}/${sessionUuidRef.current}/discard`;
        const headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(loggedUser?.token
            ? { Authorization: `Bearer ${loggedUser.token}` }
            : {}),
        };
        fetch(url, {
          method: "PUT",
          headers,
          keepalive: true,
        }).catch((err) => console.error("Discard on unmount/unload failed", err));
      }
    };

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      handleUnload();
    };
  }, [loggedUser?.token]);

  // ✅ 2) init messages once
  useEffect(() => {
    if (!sessionUuid) return;

    let cancelled = false;

    const initMessages = async () => {
      try {
        setSending(true);

        const res = await fetch(buildMessagesUrl(sessionUuid), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(loggedUser?.token
              ? { Authorization: `Bearer ${loggedUser.token}` }
              : {}),
          },
          body: JSON.stringify({ message: "-" }),
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data?.message || "Failed to init messages");
        }

        if (cancelled) return;
        appendBotMessagesFromResponse(data);
      } catch (err) {
        // optional: showToast(false, err.message || "Init failed");
      } finally {
        if (!cancelled) setSending(false);
      }
    };

    if (chat.length === 0) initMessages();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionUuid, loggedUser?.token]);

  const getImageUrl = (path) => {
    if (!path) return "";
    const cleanPath = typeof path === "string" ? path.trim() : String(path);
    if (
      cleanPath.toLowerCase().startsWith("http") ||
      cleanPath.startsWith("data:")
    )
      return cleanPath;
    return baseURL.PUBLIC_URL + cleanPath;
  };

  const handleImageSelect = async (field, url, label) => {
    if (!sessionUuid) return;

    setChat((prev) => [...prev, { from: "user", text: label }]);
    setStepCount((prev) => prev + 1);
    setCardData((prev) => ({ ...prev, [field]: url }));

    if (isVisualEditMode) {
      updateFieldVisually(field, url);
      setEditingField(null);
      return;
    }

    try {
      const res = await fetch(`${AI_CARD_SESSIONS}/${sessionUuid}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(loggedUser?.token
            ? { Authorization: `Bearer ${loggedUser.token}` }
            : {}),
        },
        body: JSON.stringify({
          message: label,
          selection: url,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Selection failed");
      
      appendBotMessagesFromResponse(data);
      fetchPreview(sessionUuid);
    } catch (err) {
      showToast(false, err.message || "Selection failed");
    }
  };

  const handleRadioSelect = async (field, value, label) => {
    if (!sessionUuid) return;

    const isCustomClick =
      value === "write_my_own" ||
      value === "pronouns" ||
      value?.toLowerCase() === "custom" ||
      label?.toLowerCase() === "custom";

    if (isCustomClick && isRestricted) {
      if (value === "write_my_own" && isVisualEditMode) {
        // Skip restriction for "write_my_own" in visual edit mode
      } else {
        setShowLimitModal(true);
        return;
      }
    }

    setChat((prev) => [...prev, { from: "user", text: label }]);
    setStepCount((prev) => prev + 1);

    if (value === "write_my_own") {
      setEditingField(field);
      setChat((prev) => [
        ...prev,
        {
          from: "bot",
          text: `Enter your Bio/Description below.`,
          ui: { field },
        },
        {
          from: "bot",
          text: "Type your answer in the input box below.",
          isInstruction: true,
        },
      ]);
      return;
    }

    const isLocalOption = EDITABLE_FIELDS.some(
      (f) => f.value === value && f.label === label
    );

    if (isLocalOption && isVisualEditMode) {
      setEditingField(value);
      setChat((prev) => [
        ...prev,
        { from: "bot", text: `Please enter the new value for ${label}:` },
      ]);
      return;
    }

    setCardData((prev) => ({ ...prev, [field]: value }));

    const isVisualField =
      ["logo", "profile_image", "background_image"].includes(field) ||
      field?.startsWith("social_media:") ||
      field?.startsWith("card_phone:") ||
      field?.startsWith("phone_extension:") ||
      EDITABLE_FIELDS.some((f) => f.value === field);

    if (isVisualEditMode && isVisualField) {
      updateFieldVisually(field, value);
      setEditingField(null);
      return;
    }

    try {
      const res = await fetch(`${AI_CARD_SESSIONS}/${sessionUuid}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(loggedUser?.token
            ? { Authorization: `Bearer ${loggedUser.token}` }
            : {}),
        },
        body: JSON.stringify({
          message: label,
          selection: value,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Selection failed");
      }

      appendBotMessagesFromResponse(data);
      fetchPreview(sessionUuid);

      // ✅ If this was a platform selection, store it (case-insensitive)
      if (label) {
        const matchedKey = Object.keys(SOCIAL_BASE_URLS).find(
          (k) => k.toLowerCase() === label.toLowerCase()
        );
        if (matchedKey) {
          setSelectedPlatform(matchedKey);
        }
      }
    } catch (err) {
      showToast(false, err.message || "Selection failed");
    }
  };

  const handleHelpAction = async (helpAction) => {
    if (!sessionUuid || !helpAction) return;

    setChat((prev) => [...prev, { from: "user", text: helpAction.label }]);
    setStepCount((prev) => prev + 1);

    try {
      const res = await fetch(`${AI_CARD_SESSIONS}/${sessionUuid}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(loggedUser?.token
            ? { Authorization: `Bearer ${loggedUser.token}` }
            : {}),
        },
        body: JSON.stringify({
          message: helpAction.value,
          selection: helpAction.value,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Action failed");
      }

      appendBotMessagesFromResponse(data);
      fetchPreview(sessionUuid);
    } catch (err) {
      showToast(false, err.message || "Action failed");
    }
  };

  const handleSend = async () => {
    const text = message.trim();
    if (!text) return;

    let currentField = editingField;
    if (!currentField) {
      const lastBotMsg = [...chat]
        .reverse()
        .find((m) => m.from === "bot" && (m.ui?.field || m.ui_elements?.field));
      currentField = lastBotMsg?.ui?.field || lastBotMsg?.ui_elements?.field;
    }

    if (
      currentField === "mobile_number" ||
      currentField === "phone_number" ||
      currentField?.startsWith("card_phone:")
    ) {
      const basicPhoneRegex = /^\+\d+/;
      if (!basicPhoneRegex.test(text.replace(/\s/g, ""))) {
        showToast(
          false,
          "You have to add country code then have to add phone number (e.g., +1 98765432)"
        );
        return;
      }
    }

    if (editingField) {
      setChat((prev) => [...prev, { from: "user", text }]);
      setStepCount((prev) => prev + 1);
      setMessage("");

      updateFieldVisually(editingField, text);
      setEditingField(null);
      return;
    }

    if (!sessionUuid) {
      showToast(false, "Session not ready yet");
      return;
    }

    setChat((prev) => [...prev, { from: "user", text }]);
    setStepCount((prev) => prev + 1);
    setMessage("");

    if (!editingField) {
      const lastBotMsg = [...chat]
        .reverse()
        .find((m) => m.from === "bot" && (m.ui?.field || m.ui_elements?.field));
      const activeField =
        lastBotMsg?.ui?.field || lastBotMsg?.ui_elements?.field;
      if (activeField) {
        setCardData((prev) => ({ ...prev, [activeField]: text }));
      }
    }

    try {
      setSending(true);

      const res = await fetch(buildMessagesUrl(sessionUuid), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(loggedUser?.token
            ? { Authorization: `Bearer ${loggedUser.token}` }
            : {}),
        },
        body: JSON.stringify({ message: text }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Failed to send message");
      }

      appendBotMessagesFromResponse(data);
      fetchPreview(sessionUuid);
    } catch (err) {
      showToast(false, err.message || "Message send failed");
    } finally {
      setSending(false);
    }
  };

  const handleUndo = async () => {
    if (!sessionUuid) return;

    setUndoSpinning(true);
    setTimeout(() => setUndoSpinning(false), 600);

    try {
      const res = await fetch(`${AI_CARD_SESSIONS}/${sessionUuid}/undo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(loggedUser?.token
            ? { Authorization: `Bearer ${loggedUser.token}` }
            : {}),
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Undo failed");

      setStepCount((prev) => Math.max(0, prev - 1));

      // After successful undo, call messages API to get re-prompt
      try {
        const msgRes = await fetch(buildMessagesUrl(sessionUuid), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(loggedUser?.token
              ? { Authorization: `Bearer ${loggedUser.token}` }
              : {}),
          },
          body: JSON.stringify({ message: "-" }),
        });
        const msgData = await msgRes.json();
        if (msgRes.ok) {
          appendBotMessagesFromResponse(msgData);
        }
      } catch (msgErr) {
        console.error("Undo re-prompt failed:", msgErr);
        // Fallback static message
        setChat((prev) => [
          ...prev,
          {
            from: "bot",
            text: "Undo successful. You have moved back one step.",
          },
        ]);
      }

      fetchPreview(sessionUuid);
    } catch (err) {
      showToast(false, err.message || "Undo failed");
    }
  };

  const handleSkip = async () => {
    if (!sessionUuid) return;

    try {
      const res = await fetch(`${AI_CARD_SESSIONS}/${sessionUuid}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(loggedUser?.token
            ? { Authorization: `Bearer ${loggedUser.token}` }
            : {}),
        },
        body: JSON.stringify({
          message: "Skip this step",
          selection: "skip",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Skip failed");

      // append bot messages
      appendBotMessagesFromResponse(data);
       
      setStepCount((prev) => prev + 1);

      fetchPreview(sessionUuid);
    } catch (err) {
      showToast(false, err.message || "Skip failed");
    }
  };

  const handleVisualEdits = async () => {
    setChat((prev) => [...prev, { from: "user", text: "Visual edits" }]);
    setStepCount((prev) => prev + 1);

    setIsVisualEditMode(true);
    setEditingField(null);

    setChat((prev) => [
      ...prev,
      { from: "bot", text: "Tap on any element in the card to edit it." },
    ]);
  };

  const handlePreviewElementClick = (field, label) => {
    if (!isVisualEditMode) return;

    setEditingField(field);

    let text = field === "description" ? `Add your ${label} below.` : `Enter your ${label} below.`;
    let isInputField = true;

    const botMsg = {
      from: "bot",
      text : text,
      ui: { field },
    };

    if (field === "background_image") {
      botMsg.text = "What background image would you like to choose?";
      botMsg.ui.type = "image_select";
      botMsg.ui.images = CardBackGroundImages.map((img) => ({
        url: img.img,
        label: `Background ${img.id}`,
      }));
      isInputField = false;
    } else if (field === "description") {
      const currentFirstName =
        cardData.first_name && cardData.first_name !== "-"
          ? cardData.first_name
          : "";
      const currentLastName =
        cardData.last_name && cardData.last_name !== "-"
          ? cardData.last_name
          : "";
      const currentName =
        cardData.name && cardData.name !== "-" ? cardData.name : "";

      const fullName = (currentFirstName || currentLastName)
        ? `${currentFirstName} ${currentLastName}`.trim()
        : currentName || (hasName ? pageUserName : "Professional");

      const fName = currentFirstName || fullName.split(" ")[0] || "Professional";
      
      const job = cardData.job_title || "Professional";
      const company = cardData.company_name || "my company";

      botMsg.ui.type = "radio";
      botMsg.ui.options = [
        {
          label: `${fullName}, ${job} at ${company}, ensuring quality and precision in every project.`,
          value: `${fullName}, ${job} at ${company}, ensuring quality and precision in every project.`,
        },
        {
          label: `Hey! I'm ${fName}, a detail-oriented ${job} at ${company}, passionate about delivering flawless software.`,
          value: `Hey! I'm ${fName}, a detail-oriented ${job} at ${company}, passionate about delivering flawless software.`,
        },
        {
          label: `${fName} here! I thrive on ${job} challenges and transforming ideas into impeccable digital experiences at ${company}.`,
          value: `${fName} here! I thrive on ${job} challenges and transforming ideas into impeccable digital experiences at ${company}.`,
        },
      ];

      botMsg.ui.options.push({
        label: "Write my own",
        value: "write_my_own",
      });

      isInputField = false;
    } else if (field === "logo" || field === "profile_image") {
      botMsg.text = `Please upload your ${label} (jpg, jpeg, png) up to 5MB.`;
      isInputField = false;
    } else if (field === "pronouns") {
      botMsg.ui.type = "radio";
      botMsg.ui.options = [
        { label: "She/Her", value: "She/Her" },
        { label: "He/Him", value: "He/Him" },
        { label: "They/Them", value: "They/Them" },
        { label: "Custom", value: "pronouns" },
      ];

      isInputField = false;
    }

    if (isInputField) {
      setChat((prev) => [
        ...prev,
        botMsg,
        {
          from: "bot",
          text: "Type your answer in the input box below.",
          isInstruction: true,
        },
      ]);

      if (field?.startsWith("social_media:")) {
        const parts = field.split(":");
        const platform = parts.length > 2 ? parts[2] : parts[1];
        
        const matchedKey = platform ? Object.keys(SOCIAL_BASE_URLS).find(
          (k) => k.toLowerCase() === platform.toLowerCase()
        ) : null;
        
        if (matchedKey && SOCIAL_BASE_URLS[matchedKey]) {
          setSelectedPlatform(matchedKey);
        setMessage(SOCIAL_BASE_URLS[matchedKey]);
        } else {
          setSelectedPlatform(null);
          setMessage("");
        }
      } else {
        setSelectedPlatform(null);
        setMessage("");
      }
    } else {
      setChat((prev) => [...prev, botMsg]);
    }
  };

  const handleResume = async () => {
    setIsVisualEditMode(false);
    setEditingField(null);

    // Resume conversation by fetching latest bot prompt
    if (sessionUuid) {
      try {
        setSending(true);
        const res = await fetch(buildMessagesUrl(sessionUuid), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(loggedUser?.token
              ? { Authorization: `Bearer ${loggedUser.token}` }
              : {}),
          },
          body: JSON.stringify({ message: "-" }),
        });

        const data = await res.json();
        if (res.ok) {
          appendBotMessagesFromResponse(data);
        }
        fetchPreview(sessionUuid);
      } catch (err) {
        console.error("Resume failed", err);
      } finally {
        setSending(false);
      }
    }
  };

  const handleDiscard = async () => {
    if (!sessionUuid) {
      navigate("/function/cards");
      return;
    }

    try {
      await fetch(`${AI_CARD_SESSIONS}/${sessionUuid}/discard`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(loggedUser?.token
            ? { Authorization: `Bearer ${loggedUser.token}` }
            : {}),
        },
      });
    } catch (err) {
      console.error("Discard failed:", err);
    } finally {
      navigate("/function/cards");
    }
  };

  const updateFieldVisually = async (field, value) => {
    if (!sessionUuid) return;

    const isImageField = ["background_image", "logo", "profile_image"].includes(
    field
  );
  const isBgImageUrl = field === "background_image" && typeof value === "string";
  const isSocialMediaField = field?.startsWith("social_media:");
    const isPhoneField = field?.startsWith("card_phone:");
    const isPhoneExtField = field?.startsWith("phone_extension:");

    let url = isBgImageUrl
    ? `${AI_CARD_SESSIONS}/${sessionUuid}/visual-edit/field`
    : isImageField
    ? `${AI_CARD_SESSIONS}/${sessionUuid}/upload`
    : isSocialMediaField
    ? `${AI_CARD_SESSIONS}/${sessionUuid}/visual-edit/social-media/${
        field.split(":")[1]
      }`
    : isPhoneField || isPhoneExtField
    ? `${AI_CARD_SESSIONS}/${sessionUuid}/visual-edit/phone/${field.split(":")[1]}`
    : `${AI_CARD_SESSIONS}/${sessionUuid}/visual-edit/field`;

  let method = isBgImageUrl
    ? "PATCH"
    : isImageField
    ? "POST"
    : isSocialMediaField
    ? "PUT"
    : isPhoneField || isPhoneExtField
    ? "PUT"
    : "PATCH";

    const headers = {
      Accept: "application/json",
      ...(loggedUser?.token
        ? { Authorization: `Bearer ${loggedUser.token}` }
        : {}),
    };

    let body;
  if (isBgImageUrl) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify({ field: field, value: value });
  } else if (isImageField) {
      body = new FormData();
      if (typeof value === "string") {
        try {
          const fullUrl = getImageUrl(value);
          const response = await fetch(fullUrl);
          const blob = await response.blob();
          body.append("image", blob, "image.png");
        } catch (err) {
          console.error("Failed to convert image to binary", err);
          body.append("image", value);
        }
      } else {
        body.append("image", value);
      }
      body.append(["logo", "profile_image"].includes(field) ? "field_name" : "field", field);
    } else {
      headers["Content-Type"] = "application/json";
      if (isSocialMediaField) {
        body = JSON.stringify({
          url: value,
          media_type: field.split(":")[2],
        });
      } else if (isPhoneField || isPhoneExtField) {
        const idx = parseInt(field.split(":")[1]);
        const p = cardData.card_phone?.[idx] || {};

        let phoneNumber = isPhoneField ? value.replace(/\D/g, "") : (p.phone_number || "").replace(/\D/g, "");
        let countryPhone = (p.country_phone || "1").replace(/\D/g, "");

        if (isPhoneField && value.includes("+")) {
          // Regex to match + followed by 1-4 digits for country code, then a separator or digits
          const matchWithSep = value.match(/^\+?(\d{1,4})\D+(.*)/);
          const matchSimple = value.match(/^\+?(\d{1,4})(.*)/);

          if (matchWithSep) {
            countryPhone = matchWithSep[1];
            phoneNumber = matchWithSep[2].replace(/\D/g, "");
          } else if (matchSimple) {
            // If no separator, be careful with greediness. 
            // If it's 12 digits starting with 91, country code is 91.
            const allDigits = value.replace(/\D/g, "");
            if (allDigits.length === 12 && allDigits.startsWith("91")) {
              countryPhone = "91";
              phoneNumber = allDigits.substring(2);
            } else {
              countryPhone = matchSimple[1];
              phoneNumber = matchSimple[2].replace(/\D/g, "");
            }
          }
        }

        body = JSON.stringify({
          phone_number: phoneNumber,
          country_phone: countryPhone,
          phone_type: p.phone_type || "mobile",
          phone_extension: isPhoneExtField ? value : p.phone_extension,
        });
      } else {
        body = JSON.stringify({ field: field, value: value });
      }
    }

    try {
      const res = await fetch(url, {
        method,
        headers,
        body,
      });

      const data = await res.json();

      if (res.ok) {
        if (data?.data?.card_data) {
          setCardData(data.data.card_data);
        } else {
          setCardData((prev) => ({ ...prev, [field]: value }));
        }

        showToast(true, data?.message || "Updated successfully");
        fetchPreview(sessionUuid);

        let friendlyField = field
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase());

        if (field.startsWith("card_phone:")) {
          const idx = parseInt(field.split(":")[1]);
          const type = cardData.card_phone?.[idx]?.phone_type || "";
          friendlyField = `${
            type ? type.charAt(0).toUpperCase() + type.slice(1) + " " : ""
          }Phone Number`;
        } else if (field.startsWith("phone_extension:")) {
          const idx = parseInt(field.split(":")[1]);
          const type = cardData.card_phone?.[idx]?.phone_type || "";
          friendlyField = `${
            type ? type.charAt(0).toUpperCase() + type.slice(1) + " " : ""
          }Phone Extension`;
        } else if (field.startsWith("social_media:")) {
          const parts = field.split(":");
          const type = parts[2] || "Social";
          friendlyField = `${type.charAt(0).toUpperCase() + type.slice(1)} Link`;
        }
        setChat((prev) => [
          ...prev,
          { from: "bot", text: `${friendlyField} updated successfully.` },
        ]);
      } else {
        throw new Error(data?.message || "Update failed");
      }
    } catch (err) {
      showToast(false, err.message || "Update failed");
    }
  };

  const toggleListen = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      showToast(false, "Speech recognition is not supported in this browser.");
      return;
    }

    if (isListeningRef.current) {
      isListeningRef.current = false;
      setIsListening(false);
      recognitionRef.current?.stop();
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => {
      isListeningRef.current = true;
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      let finalTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        }
      }

      if (finalTranscript) {
        setMessage((prev) => {
          const separator = prev && !prev.endsWith(" ") ? " " : "";
          return prev + separator + finalTranscript;
        });
      }
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error", event.error);
      if (event.error !== "no-speech") {
        isListeningRef.current = false;
        setIsListening(false);
        showToast(false, `Speech error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      if (isListeningRef.current) {
        try {
          recognition.start();
        } catch (e) {
          console.error("Failed to restart recognition", e);
        }
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const isSkeleton = sessionLoading;

  useEffect(() => {
    if (sessionUuid) {
      fetchPreview(sessionUuid);
    }
  }, [sessionUuid]);

  const activeBotMsg = useMemo(() => {
    return [...chat]
      .reverse()
      .find((m) => m.from === "bot" && (m.ui?.field || m.ui_elements?.field));
  }, [chat]);

  const activeField =
    activeBotMsg?.ui?.field || activeBotMsg?.ui_elements?.field;

  const isFieldActive = (field, aliases = []) => {
    const all = [field, ...aliases];
    return all.includes(activeField);
  };

  // ✅ Pre-fill social media URL if platform is selected
  useEffect(() => {
    if ((activeField === "social_media_url" || activeField?.startsWith("social_media:")) && selectedPlatform) {
      const baseUrl = SOCIAL_BASE_URLS[selectedPlatform];
      if (baseUrl && !message.startsWith(baseUrl)) {
        setMessage(baseUrl);
      }
    }
  }, [activeField, selectedPlatform]);

  const getPreviewValue = (field, aliases = []) => {
    const allFields = [field, ...aliases];

    if (message && allFields.includes(activeField)) {
      return message;
    }

    for (const f of allFields) {
      if (cardData[f]) {
        const val = cardData[f];
        if (
          typeof val === "string" &&
          (val.toLowerCase() === "start" || val === "-")
        ) {
          return "";
        }
        return val;
      }
    }
    return null;
  };

  const previewName = useMemo(() => {
    const sanitize = (val) =>
      typeof val === "string" && (val.toLowerCase() === "start" || val === "-")
        ? ""
        : val || "";

    const firstName = sanitize(cardData.first_name);
    const lastName = sanitize(cardData.last_name);
    const name = sanitize(cardData.name);

    if (activeField === "name" && message) return message;
    if (activeField === "first_name" && message)
      return `${message} ${lastName}`.trim();
    if (activeField === "last_name" && message)
      return `${firstName} ${message}`.trim();

    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return name || (hasName ? pageUserName : "");
  }, [activeField, message, cardData, hasName, pageUserName]);

  return (
    <>
      <div className="ai-chat-page">
      <div className="ai-chat-shell">
        {/* LEFT */}
        <div className="ai-chat-left">
          <div className="ai-chat-list">
            <div className="ai-bot-header">
              <img src={aiLogo} alt="Bot" className="ai-bot-icon" />
              <span className="ai-bot-name">Link</span>
              <span className="ai-bot-time">{formatTime()}</span>
            </div>
            <div className="ai-row ai-row-bot">
              <div className="ai-bubble">
                Your name is set to{" "}
                <b>
                  {cardData.first_name || cardData.last_name
                    ? `${cardData.first_name || ""} ${
                        cardData.last_name || ""
                      }`.trim()
                    : cardData.name || (hasName ? pageUserName : "—")}
                </b>
                .
              </div>
            </div>

            {chat.map((m, idx) => (
              <div key={idx}>
                {m.from === "bot" && !m.isInstruction && (
                  <div className="ai-bot-header">
                    <img src={aiLogo} alt="Bot" className="ai-bot-icon" />
                    <span className="ai-bot-name">Link</span>
                    <span className="ai-bot-time">
                      {formatTime(m.timestamp)}
                    </span>
                  </div>
                )}

                <div
                  className={`ai-row ${
                    m.from === "user" ? "ai-row-user" : "ai-row-bot"
                  }`}
                >
                  <div
                    className={`ai-bubble ${
                      m.from === "user" ? "ai-bubble-user" : ""
                    } ${m.isInstruction ? "ai-instruction" : ""}`}
                  >
                    {m.text}
                    {m.image && (
                      <div className="ai-chat-img-wrap">
                        <img
                          src={getImageUrl(m.image)}
                          alt="Uploaded"
                          className="ai-chat-img"
                        />
                      </div>
                    )}
                    {m.isSuccessImage && (
                      <div className="ai-success-msg-wrap">
                        <img
                          src={cardSuccessIcon}
                          alt="Success"
                          className="ai-success-img"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {m.from === "bot" && m.ui?.type === "radio" && (
                  <div className="ai-row ai-row-bot ai-option-row">
                    <div className="ai-option-group">
                      {m.ui.options
                        .map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            className="ai-option-btn"
                            onClick={() =>
                              handleRadioSelect(
                                m.ui.field,
                                opt.value,
                                opt.label
                              )
                            }
                          >
                            {opt.label}
                          </button>
                        ))}
                    </div>
                  </div>
                )}

                {m.ui?.type === "image_select" && (
                  <div className="ai-bg-grid">
                    {m.ui.images &&
                      m.ui.images.map((img, i) => (
                        <div
                          key={i}
                          className={`ai-bg-option ${
                            cardData[m.ui.field] === img.url ? "active" : ""
                          }`}
                          onClick={() =>
                            handleImageSelect(m.ui.field, img.url, img.label)
                          }
                        >
                          <img src={getImageUrl(img.url)} alt={img.label} />
                        </div>
                      ))}
                  </div>
                )}

                {m.from === "bot" && m.ui?.help_action && (
                  <div className="ai-row ai-row-bot ai-option-row">
                    <div className="ai-option-group">
                      <button
                        type="button"
                        className="ai-help-btn"
                        onClick={() => handleHelpAction(m.ui.help_action)}
                      >
                        <FiHelpCircle size={14} /> {m.ui.help_action.label}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="ai-row ai-row-bot">
                <div className="ai-typing-indicator">
                  <span className="ai-typing-dot"></span>
                  <span className="ai-typing-dot"></span>
                  <span className="ai-typing-dot"></span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Undo Button */}
          <div
            style={{
              padding: "0 1rem 0",
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <button
                onClick={handleSkip}
                disabled={sessionLoading || isVisualEditMode || sending || !canSkip}
                className="ai-pill-btn"
                style={{ fontSize: "0.85rem", padding: "6px 12px", marginLeft: "10px" }}
              >
                <IoPlaySkipForwardOutline /> Skip
              </button>

            <button
              onClick={handleUndo}
              disabled={sessionLoading || stepCount === 0 || isVisualEditMode}
              className="ai-pill-btn"
              style={{ fontSize: "0.85rem", padding: "6px 12px" }}
            >
              <FiRotateCcw className={undoSpinning ? "undo-spin" : ""} /> Undo
            </button>
            
          </div>

          {/* Composer */}
          <div className="ai-composer">
            {(() => {
              const lastMsg = chat[chat.length - 1];
              const shouldHighlightInput = lastMsg?.isInstruction;
              return (
                <div
                  className={`ai-composer-bar ${
                    shouldHighlightInput ? "highlight-input" : ""
                  }`}
                >
                  <div className="ai-composer-top">
                    <div className="ai-composer-middle">
                      <input
                        className="ai-compose-input"
                        placeholder="Ask Link.."
                        value={message}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (
                            (activeField === "social_media_url" ||
                              activeField?.startsWith("social_media:")) &&
                            selectedPlatform
                          ) {
                            const baseUrl = SOCIAL_BASE_URLS[selectedPlatform];
                            if (baseUrl && !val.startsWith(baseUrl)) {
                              return; // Prevent deleting base URL
                            }
                          }
                          setMessage(val);
                        }}
                        onKeyDown={(e) => e.key === "Enter" && handleSend()}
                        disabled={sessionLoading || sending}
                      />
                    </div>
                  </div>

                  <div className="ai-composer-buttom">
                    <div className="ai-composer-left">
                      <input
                        type="file"
                        ref={fileInputRef}
                        hidden
                        accept=".jpg, .jpeg, .png"
                        onChange={handleFileUpload}
                      />
                      <button
                        className="ai-pill-btn"
                        type="button"
                        onClick={() =>
                          fileInputRef.current && fileInputRef.current.click()
                        }
                      >
                        <FiPaperclip /> <span>Attach</span>
                      </button>

                      <button
                        className={`ai-pill-btn ${
                          isVisualEditMode ? "" : "ai-pill-muted"
                        }`}
                        type="button"
                        onClick={handleVisualEdits}
                        disabled={sessionLoading || sending}
                        style={
                          isVisualEditMode
                            ? {
                                backgroundColor: "#21b38a",
                                color: "#fff",
                                borderColor: "#21b38a",
                              }
                            : {}
                        }
                      >
                        <GiPencilBrush /> <span>Visual edits</span>
                      </button>
                    </div>

                    <div className="ai-composer-right">
                      <button
                        className={`ai-icon-circle ai-mic ${
                          isListening ? "active" : ""
                        }`}
                        type="button"
                        aria-label="Mic"
                        onClick={toggleListen}
                        disabled={
                          sessionLoading ||
                          sending ||
                          activeBotMsg?.ui?.type === "radio" ||
                          activeBotMsg?.ui?.type === "image_select"
                        }
                      >
                        <FiMic />
                      </button>

                      <button
                        className="ai-icon-circle ai-send"
                        type="button"
                        onClick={handleSend}
                        disabled={sessionLoading || sending}
                        aria-label="Send"
                      >
                        <FiArrowUp />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* RIGHT */}
        <div className="ai-chat-right">
          <div className="ai-preview-actions">
            <div className="ai-preview-actions-left">
              <button
                className="ai-action-btn-primary"
                disabled={sessionLoading}
              >
                <span className="ai-play-icon">▶</span> Preview
              </button>

              <button
                className={
                  isVisualEditMode
                    ? "ai-action-btn-primary"
                    : "ai-action-btn-secondary"
                }
                disabled={sessionLoading}
                onClick={handleVisualEdits}
                style={isVisualEditMode ? { backgroundColor: "#21b38a", color: "#fff", borderColor: "#21b38a" } : {}}
              >
                <GiPencilBrush /> Visual edits
              </button>

              {isVisualEditMode && (
                <button
                  className="ai-action-btn-secondary"
                  onClick={handleResume}
                >
                  <IoMdArrowBack /> Back To Chat
                </button>
              )}
            </div>

            <div style={{ position: "relative" }}>
              <button
                className="ai-theme-btn"
                disabled={sessionLoading}
                onClick={handleThemeClick}
              >
                <img src={theme} alt="Theme" /> Theme
              </button>

              {showThemePicker && (
                <div className="ai-theme-picker">
                  <div className="ai-theme-grid">
                    {presetColors.map((c) => (
                      <div
                        key={c}
                        className="ai-color-swatch"
                        style={{ backgroundColor: c }}
                        onClick={() => setTempColor(c)}
                      >
                        {tempColor === c && <FiCheck color="#fff" size={16} />}
                      </div>
                    ))}
                  </div>
                  <button
                    className="ai-confirm-btn"
                    onClick={handleConfirmTheme}
                  >
                    Confirm
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className={`ai-preview-card ${isSkeleton ? "is-skeleton" : ""}`}>
            <div
              style={{
                backgroundImage: cardData.background_image
                  ? `url(${getImageUrl(cardData.background_image)})`
                  : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              className={`ai-preview-top ${
                isVisualEditMode && cardData.background_image
                  ? "visual-editable"
                  : ""
              }`}
              onClick={() =>
                cardData.background_image &&
                handlePreviewElementClick(
                  "background_image",
                  "Background Image URL"
                )
              }
            ></div>

            <div className="ai-preview-body">
              <div className="ai-avatar-wrapper">
                <div
                  style={{
                    borderRadius:
                      cardData.profile_image_shape?.toLowerCase() === "square"
                        ? "0px"
                        : cardData.profile_image_shape?.toLowerCase() ===
                          "rectangular"
                        ? "12px"
                        : "50%",
                    backgroundImage: cardData.profile_image
                      ? `url(${getImageUrl(cardData.profile_image)})`
                      : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundColor: cardData.profile_image
                      ? "transparent"
                      : "#e0e0e0",
                  }}
                  className={`ai-avatar-circle ${
                    isVisualEditMode && cardData.profile_image
                      ? "visual-editable"
                      : ""
                  }`}
                  onClick={() =>
                    cardData.profile_image &&
                    handlePreviewElementClick(
                      "profile_image",
                      "Profile Image URL"
                    )
                  }
                ></div>

                {cardData.logo && (
                  <div
                    className={`ai-logo-badge-img ${
                      isVisualEditMode ? "visual-editable" : ""
                    }`}
                    style={{
                      backgroundImage: `url(${getImageUrl(cardData.logo)})`,
                      borderRadius:
                        cardData.logo_shape?.toLowerCase() === "square"
                          ? "0px"
                          : cardData.logo_shape?.toLowerCase() === "rectangular"
                          ? "8px"
                          : "50%",
                    }}
                    onClick={() =>
                      handlePreviewElementClick("logo", "Logo URL")
                    }
                  ></div>
                )}
              </div>

              {(() => {
                const themeColor = cardData.color || "#7000B5";
                return (
                  <>
                    <div
                      className="ai-preview-badge"
                      style={{ color: themeColor }}
                    >
                      {getPreviewValue("card_type") ||
                        cardData.card_type ||
                        "Personal"}
                    </div>

                    <div
                      className="ai-preview-name"
                      style={{ color: themeColor }}
                    >
                      {(() => {
                        let fName = getPreviewValue("first_name");
                        let lName = getPreviewValue("last_name");

                        if (!fName && !lName) {
                          const fullName =
                            getPreviewValue("name") ||
                            (hasName ? pageUserName : "");
                          const parts = fullName.trim().split(" ");
                          if (parts.length > 0) {
                            fName = parts[0];
                            lName = parts.slice(1).join(" ");
                          }
                        }

                        return (
                          <>
                            <span
                              className={`${vSelected(
                                isFieldActive("first_name")
                              )} ${
                                isVisualEditMode && fName
                                  ? "visual-editable"
                                  : ""
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                fName &&
                                  handlePreviewElementClick(
                                    "first_name",
                                    "First Name"
                                  );
                              }}
                              style={{
                                display: "inline-block",
                                marginRight: "4px",
                              }}
                            >
                              {fName}
                            </span>

                            <span
                              className={`${vSelected(
                                isFieldActive("last_name")
                              )} ${
                                isVisualEditMode && lName
                                  ? "visual-editable"
                                  : ""
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                lName &&
                                  handlePreviewElementClick(
                                    "last_name",
                                    "Last Name"
                                  );
                              }}
                              style={{ display: "inline-block" }}
                            >
                              {lName}
                            </span>
                          </>
                        );
                      })()}

                      {(() => {
                        const val = getPreviewValue("pronouns");
                        if (
                          val === "no" ||
                          (cardData.pronouns === "start" && !val)
                        ) {
                          return <div className={`ai-line w40`}></div>;
                        }
                        if (val) {
                          return (
                            <div
                              className={`ai-preview-pronouns ${vSelected(
                                isFieldActive("pronouns")
                              )} ${isVisualEditMode ? "visual-editable" : ""}`}
                              style={{ color: themeColor }}
                              onClick={() =>
                                handlePreviewElementClick(
                                  "pronouns",
                                  "Pronouns"
                                )
                              }
                            >
                              ({val})
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </>
                );
              })()}

              <div className="ai-preview-lines">
                {getPreviewValue("company", [
                  "company_name",
                  "business_name",
                ]) ? (
                  <div
                    className={`ai-line-text ${vSelected(
                      isFieldActive("company", [
                        "company_name",
                        "business_name",
                      ])
                    )} ${isVisualEditMode ? "visual-editable" : ""}`}
                    onClick={() =>
                      handlePreviewElementClick("company", "Company Name")
                    }
                  >
                    {getPreviewValue("company", [
                      "company_name",
                      "business_name",
                    ])}
                  </div>
                ) : (
                  <div className={`ai-line w40`}></div>
                )}

                {getPreviewValue("title", [
                  "job_title",
                  "role",
                  "company_position",
                ]) ? (
                  <div
                    className={`ai-line-text ${vSelected(
                      isFieldActive("title", [
                        "job_title",
                        "role",
                        "company_position",
                      ])
                    )} ${isVisualEditMode ? "visual-editable" : ""}`}
                    onClick={() =>
                      handlePreviewElementClick("company_position", "Job Title")
                    }
                  >
                    {getPreviewValue("title", [
                      "job_title",
                      "role",
                      "company_position",
                    ])}
                  </div>
                ) : (
                  <div className={`ai-line w40`}></div>
                )}

                {!getPreviewValue("title", [
                  "job_title",
                  "role",
                  "company_position",
                ]) &&
                  !getPreviewValue("company", [
                    "company_name",
                    "business_name",
                  ]) && <div className={`ai-line w100`}></div>}

                {getPreviewValue("description") ? (
                  <div
                    className={`ai-line-text ${vSelected(
                      isFieldActive("description")
                    )} ${isVisualEditMode ? "visual-editable" : ""}`}
                    style={{
                      whiteSpace: "pre-wrap",
                      textAlign: "center",
                      fontSize: "13px",
                      color: "var(--primary_text_color)",
                      marginTop: "10px",
                    }}
                    onClick={() =>
                      handlePreviewElementClick(
                        "description",
                        "Bio/Description"
                      )
                    }
                  >
                    {getPreviewValue("description")}
                  </div>
                ) : (
                  <div className={`ai-line w100`}></div>
                )}
              </div>

              <div
                className={`ai-preview-section ${
                  cardData.content_view_type || "listDesign"
                }`}
              >
                <h4 className="ai-line w50">Contact Info</h4>

                <div
                  className={`ai-contact-view ${
                    cardData.content_view_type || "listDesign"
                  }`}
                >
                  {/* Email Section */}
                  <div
                    className={`ai-contact-item ${vSelected(
                      isFieldActive("email", [
                        "email_address",
                        "contact_email",
                        "email_id",
                        "user_email",
                      ])
                    )} ${
                      isVisualEditMode &&
                      cardData.content_view_type === "iconDesign"
                        ? "visual-editable"
                        : ""
                    }`}
                    onClick={() => {
                      if (
                        isVisualEditMode &&
                        cardData.content_view_type === "iconDesign"
                      ) {
                        handlePreviewElementClick("email", "Email Address");
                      }
                    }}
                  >
                    {getPreviewValue("email", [
                      "email_address",
                      "contact_email",
                      "email_id",
                      "user_email",
                    ]) ? (
                      <>
                        <a
                          href={isVisualEditMode ? undefined : `mailto:${getPreviewValue("email", [
                            "email_address",
                            "contact_email",
                            "email_id",
                            "user_email",
                          ])}`}
                          style={{
                            backgroundColor: cardData.color || "#7000B5",
                          }}
                          className="ai-icon-wrap"
                        >
                          <FiMail size={14} color="#fff" />
                        </a>
                        <a
                          href={isVisualEditMode ? undefined : `mailto:${getPreviewValue("email", [
                            "email_address",
                            "contact_email",
                            "email_id",
                            "user_email",
                          ])}`}
                          onClick={(e) => {
                            if (
                              isVisualEditMode &&
                              cardData.content_view_type !== "iconDesign"
                            ) {
                              e.preventDefault();
                              handlePreviewElementClick(
                                "email",
                                "Email Address"
                              );
                            }
                          }}
                          className={`ai-line-text ${
                            isVisualEditMode &&
                            cardData.content_view_type !== "iconDesign"
                              ? "visual-editable"
                              : ""
                          }`}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          {getPreviewValue("email", [
                            "email_address",
                            "contact_email",
                            "email_id",
                            "user_email",
                          ])}
                        </a>
                      </>
                    ) : (
                      <>
                        <div className="ai-dot"></div>
                        <div className="ai-line w80"></div>
                      </>
                    )}
                  </div>

                  {/* Website Section */}
                  <div
                    className={`ai-contact-item ${vSelected(
                      isFieldActive("website", [
                        "url",
                        "link",
                        "web",
                        "web_address",
                        "website_url",
                      ])
                    )} ${
                      isVisualEditMode &&
                      cardData.content_view_type === "iconDesign"
                        ? "visual-editable"
                        : ""
                    }`}
                    onClick={() => {
                      if (
                        isVisualEditMode &&
                        cardData.content_view_type === "iconDesign"
                      ) {
                        handlePreviewElementClick("website_url", "Website URL");
                      }
                    }}
                  >
                    {getPreviewValue("website", [
                      "url",
                      "link",
                      "web",
                      "web_address",
                      "website_url",
                    ]) ? (
                      <>
                        <a
                          style={{
                            backgroundColor: cardData.color || "#7000B5",
                          }}
                          className="ai-icon-wrap"
                          href={isVisualEditMode ? undefined : (
                            String(
                              getPreviewValue("website", [
                                "url",
                                "link",
                                "web",
                                "web_address",
                                "website_url",
                              ])
                            ).startsWith("http")
                              ? getPreviewValue("website", [
                                  "url",
                                  "link",
                                  "web",
                                  "web_address",
                                  "website_url",
                                ])
                              : `https://${getPreviewValue("website", [
                                  "url",
                                  "link",
                                  "web",
                                  "web_address",
                                  "website_url",
                                ])}`
                          )}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <FiGlobe size={14} color="#fff" />
                        </a>
                        <a
                          href={isVisualEditMode ? undefined : (
                            String(
                              getPreviewValue("website", [
                                "url",
                                "link",
                                "web",
                                "web_address",
                                "website_url",
                              ])
                            ).startsWith("http")
                              ? getPreviewValue("website", [
                                  "url",
                                  "link",
                                  "web",
                                  "web_address",
                                  "website_url",
                                ])
                              : `https://${getPreviewValue("website", [
                                  "url",
                                  "link",
                                  "web",
                                  "web_address",
                                  "website_url",
                                ])}`
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            if (
                              isVisualEditMode &&
                              cardData.content_view_type !== "iconDesign"
                            ) {
                              e.preventDefault();
                              handlePreviewElementClick(
                                "website_url",
                                "Website URL"
                              );
                            }
                          }}
                          className={`ai-line-text ${
                            isVisualEditMode &&
                            cardData.content_view_type !== "iconDesign"
                              ? "visual-editable"
                              : ""
                          }`}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          {getPreviewValue("website", [
                            "url",
                            "link",
                            "web",
                            "web_address",
                            "website_url",
                          ])}
                        </a>
                      </>
                    ) : (
                      <>
                        <div className="ai-dot"></div>
                        <div className="ai-line w80"></div>
                      </>
                    )}
                  </div>

                  {/* Location Section - Granular */}
                  <div className="ai-contact-item">
                    {(() => {
                      const addr =
                        getPreviewValue("address") ||
                        getPreviewValue("location");
                      const city = getPreviewValue("city");
                      const state = getPreviewValue("state");
                      const country = getPreviewValue("country");

                      const parts = [];
                      if (addr)
                        parts.push({
                          val: addr,
                          key: "address",
                          label: "Street Address",
                        });
                      if (city)
                        parts.push({ val: city, key: "city", label: "City" });
                      if (state)
                        parts.push({
                          val: state,
                          key: "state",
                          label: "State/Province",
                        });
                      if (country)
                        parts.push({
                          val: country,
                          key: "country",
                          label: "Country",
                        });

                      const validParts = parts.filter((p) => p.val);
                      const fullLocString = validParts
                        .map((p) => p.val)
                        .join(", ");

                      if (validParts.length > 0) {
                        return (
                            <div
                              className={`ai-contact-item ${
                                isVisualEditMode &&
                                cardData.content_view_type === "iconDesign"
                                  ? "visual-editable"
                                  : ""
                              }`}
                              onClick={() => {
                                if (
                                  isVisualEditMode &&
                                  cardData.content_view_type === "iconDesign"
                                ) {
                                  handlePreviewElementClick(
                                    "address",
                                    "Street Address"
                                  );
                                }
                              }}
                            >
                              <a
                                href={isVisualEditMode ? undefined : `http://maps.google.com/?q=${fullLocString}`}
                                style={{
                                  backgroundColor: cardData.color || "#7000B5",
                                }}
                                className="ai-icon-wrap"
                                target="_blank"
                                rel="noreferrer"
                              >
                                <FiMapPin size={14} color="#fff" />
                              </a>
                              <div
                                className="ai-line-text"
                                style={{ cursor: "default" }}
                              >
                                {validParts.map((part, i) => (
                                  <span key={part.key}>
                                    <span
                                      className={`${vSelected(
                                        isFieldActive(part.key)
                                      )} ${
                                        isVisualEditMode &&
                                        cardData.content_view_type !==
                                          "iconDesign"
                                          ? "visual-editable"
                                          : ""
                                      }`}
                                      onClick={(e) => {
                                        if (
                                          isVisualEditMode &&
                                          cardData.content_view_type !==
                                            "iconDesign"
                                        ) {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handlePreviewElementClick(
                                            part.key,
                                            part.label
                                          );
                                        }
                                      }}
                                    >
                                      {part.val}
                                    </span>
                                    {i < validParts.length - 1 && ", "}
                                  </span>
                                ))}
                              </div>
                            </div>
                        );
                      } else {
                        return (
                          <>
                            <div className="ai-dot"></div>
                            <div className="ai-line w80"></div>
                          </>
                        );
                      }
                    })()}
                  </div>

                  {/* Phone Section */}
                  {cardData.card_phone &&
                  Array.isArray(cardData.card_phone) &&
                  cardData.card_phone.length > 0 ? (
                    cardData.card_phone.map((p, idx) => {
                      let Icon = FiSmartphone;
                      const type = p.phone_type?.toLowerCase();
                      if (type === "home") Icon = FiHome;
                      if (type === "work" || type === "office")
                        Icon = FiHeadphones;

                      const cleanNumber = String(p.phone_number || "").replace(/\D/g, "");
                      const telLink = `tel:+${p.country_phone}${cleanNumber}`;

                      const prefix = p.country_phone ? `+${p.country_phone}` : "";
                      const displayNumber = `${prefix}${p.phone_number || ""}${
                        p.phone_extension ? ` (Ext: ${p.phone_extension})` : ""
                      }`;

                      const compositeField = `card_phone:${idx}`;
                      const extField = `phone_extension:${idx}`;

                      return (
                        <div
                          key={idx}
                          className={`ai-contact-item ${vSelected(
                            isFieldActive(compositeField, [
                              "phone",
                              "phone_number",
                              "mobile",
                              "contact",
                              p.phone_number,
                            ])
                          )} ${
                            isVisualEditMode &&
                            cardData.content_view_type === "iconDesign"
                              ? "visual-editable"
                              : ""
                          }`}
                          onClick={() => {
                            if (
                              isVisualEditMode &&
                              cardData.content_view_type === "iconDesign"
                            ) {
                              handlePreviewElementClick(
                                compositeField,
                                "Phone Number"
                              );
                            }
                          }}
                        >
                          <a
                            href={isVisualEditMode ? undefined : telLink}
                            style={{
                              backgroundColor: cardData.color || "#7000B5",
                            }}
                            className="ai-icon-wrap"
                            title={displayNumber}
                          >
                            <Icon size={14} color="#fff" />
                          </a>
                          <div
                            className="ai-line-text"
                            style={{ 
                              textDecoration: "none", 
                              color: "inherit",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center"
                            }}
                          >
                            <span 
                              style={{ 
                                fontSize: "12px", 
                                fontWeight: "600", 
                                color: cardData.color || "#7000B5",
                                marginBottom: "2px" 
                              }}
                            >
                              {p.phone_type ? p.phone_type.charAt(0).toUpperCase() + p.phone_type.slice(1) : "Phone"}
                            </span>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <span
                                className={`${vSelected(
                                  isFieldActive(compositeField, [
                                    "phone",
                                    "phone_number",
                                    "mobile",
                                    "contact",
                                    p.phone_number,
                                  ])
                                )} ${
                                  isVisualEditMode &&
                                  cardData.content_view_type !== "iconDesign"
                                    ? "visual-editable"
                                    : ""
                                }`}
                                onClick={(e) => {
                                  if (
                                    isVisualEditMode &&
                                    cardData.content_view_type !== "iconDesign"
                                  ) {
                                    e.stopPropagation();
                                    handlePreviewElementClick(
                                      compositeField,
                                      "Phone Number"
                                    );
                                  }
                                }}
                              >
                                {`${prefix}${formatPhoneNumber(
                                  getPreviewValue(compositeField) ||
                                  p.phone_number ||
                                  ""
                                )}`}
                              </span>
                            {(p.phone_type === "work" ||
                              p.phone_type === "office" ||
                              p.phone_extension) && (
                              <span
                                className={`${vSelected(
                                  isFieldActive(extField, [
                                    "phone_extension",
                                    p.phone_extension,
                                  ])
                                )} ${
                                  isVisualEditMode &&
                                  cardData.content_view_type !== "iconDesign"
                                    ? "visual-editable"
                                    : ""
                                }`}
                                onClick={(e) => {
                                  if (
                                    isVisualEditMode &&
                                    cardData.content_view_type !== "iconDesign"
                                  ) {
                                    e.stopPropagation();
                                    handlePreviewElementClick(
                                      extField,
                                      "Phone Extension"
                                    );
                                  }
                                }}
                                style={{ marginLeft: "4px" }}
                              >
                                {` (Ext: ${
                                  getPreviewValue(extField) ||
                                  p.phone_extension ||
                                  "Add"
                                })`}
                              </span>
                            )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div
                      className={`ai-contact-item ${vSelected(
                        isFieldActive("phone", [
                          "phone_number",
                          "mobile",
                          "contact",
                        ])
                      )}`}
                      onClick={() =>
                        getPreviewValue("phone", [
                          "phone_number",
                          "mobile",
                          "contact",
                        ]) &&
                        handlePreviewElementClick("mobile_number", "Phone")
                      }
                    >
                      {getPreviewValue("phone", [
                        "phone_number",
                        "mobile",
                        "contact",
                      ]) ? (
                        <>
                          <a
                            href={isVisualEditMode ? undefined : `tel:${formatPhoneNumber(
                              getPreviewValue("phone", [
                                "card_phone",
                                "mobile_number",
                                "phone_number",
                                "mobile",
                                "contact",
                              ])
                            )}`}
                            style={{
                              backgroundColor: cardData.color || "#7000B5",
                            }}
                            className="ai-icon-wrap"
                          >
                            <FiSmartphone size={14} color="#fff" />
                          </a>
                          <a
                            href={isVisualEditMode ? undefined : `tel:${formatPhoneNumber(
                              getPreviewValue("phone", [
                                "card_phone",
                                "mobile_number",
                                "phone_number",
                                "mobile",
                                "contact",
                              ])
                            )}`}
                            className={`ai-line-text ${
                              isVisualEditMode &&
                              getPreviewValue("phone", [
                                "phone_number",
                                "mobile",
                                "contact",
                              ])
                                ? "visual-editable"
                                : ""
                            }`}
                            style={{ 
                              textDecoration: "none", 
                              color: "inherit",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center"
                            }}
                          >
                            <span 
                              style={{ 
                                fontSize: "12px", 
                                fontWeight: "600", 
                                color: cardData.color || "#7000B5",
                                marginBottom: "2px" 
                              }}
                            >
                              Phone
                            </span>
                            <span style={{ display: "flex", alignItems: "center" }}>
                              {`${getPreviewValue("phone", [
                                    "card_phone",
                                    "mobile_number",
                                    "phone_number",
                                    "mobile",
                                    "contact",
                                  ]) || ""}`.replace(/[\s\(\)\-]/g, "")}
                            </span>
                          </a>
                        </>
                      ) : (
                        <>
                          <div className="ai-dot"></div>
                          <div className="ai-line w80"></div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div
                className={`ai-preview-section ${
                  cardData.content_view_type || "listDesign"
                }`}
              >
                <h4 className="ai-line w50">Social Profile</h4>
                <div
                  className={`ai-contact-view ${
                    cardData.content_view_type || "listDesign"
                  }`}
                >
                  {cardData.card_social_media &&
                  Array.isArray(cardData.card_social_media) &&
                  cardData.card_social_media.length > 0 ? (
                    cardData.card_social_media.map((social, idx) => {
                      const rawType = (
                        social.social_type ||
                        social.type ||
                        social.media_type ||
                        ""
                      ).toLowerCase();
                      const rawUrl = social.social_url || social.url || "";
                      const url = rawUrl.toLowerCase();

                      const isType = (key) =>
                        rawType.includes(key) || url.includes(key);

                      let Icon = FiLink;
                      let platformName = "Custom";
                      let displayLabel = "Social Link";

                      if (isType("facebook")) {
                        Icon = FaFacebookF;
                        platformName = "Facebook";
                        displayLabel = "Facebook URL";
                      } else if (isType("twitter") || isType("x.com")) {
                        Icon = FaTwitter;
                        platformName = "Twitter";
                        displayLabel = "Twitter URL";
                      } else if (isType("linkedin")) {
                        Icon = FaLinkedinIn;
                        platformName = "LinkedIn";
                        displayLabel = "LinkedIn URL";
                      } else if (isType("instagram")) {
                        Icon = FaInstagram;
                        platformName = "Instagram";
                        displayLabel = "Instagram URL";
                      } else if (isType("youtube")) {
                        Icon = FaYoutube;
                        platformName = "Youtube";
                        displayLabel = "YouTube URL";
                      } else if (isType("github")) {
                        Icon = FaGithub;
                        platformName = "Github";
                        displayLabel = "GitHub URL";
                      } else if (isType("tiktok")) {
                        Icon = FaTiktok;
                        platformName = "Tiktok";
                        displayLabel = "TikTok URL";
                      } else if (isType("snapchat")) {
                        Icon = FaSnapchatGhost;
                        platformName = "SnapChat";
                        displayLabel = "Snapchat URL";
                      } else if (isType("pinterest")) {
                        Icon = FaPinterestP;
                        platformName = "Pinterest";
                        displayLabel = "Pinterest URL";
                      } else if (isType("whatsapp")) {
                        Icon = FaWhatsapp;
                        platformName = "Whatsapp";
                        displayLabel = "WhatsApp URL";
                      } else if (isType("telegram") || isType("t.me")) {
                        Icon = FaTelegramPlane;
                        platformName = "Telegram";
                        displayLabel = "Telegram URL";
                      } else if (isType("spotify")) {
                        Icon = FaSpotify;
                        platformName = "Spotify";
                        displayLabel = "Spotify URL";
                      } else if (isType("vimeo")) {
                        Icon = FaVimeoV;
                        platformName = "Vimeo";
                        displayLabel = "Vimeo URL";
                      } else if (isType("dribbble")) {
                        Icon = FaDribbble;
                        platformName = "Dribble";
                        displayLabel = "Dribbble URL";
                      } else if (isType("soundcloud")) {
                        Icon = FaSoundcloud;
                        platformName = "SoundCloud";
                        displayLabel = "SoundCloud URL";
                      } else if (isType("google")) {
                        Icon = GrAndroid;
                        platformName = "Android";
                        displayLabel = "Google URL";
                      } else if (isType("messenger")) {
                        Icon = FaFacebookMessenger;
                        platformName = "Messenger";
                        displayLabel = "Messenger URL";
                      } else if (isType("skype")) {
                        Icon = FaSkype;
                        platformName = "Skype";
                        displayLabel = "Skype URL";
                      } else if (isType("dropbox")) {
                        Icon = FaDropbox;
                        platformName = "Dropbox";
                        displayLabel = "Dropbox URL";
                      } else if (isType("wordpress")) {
                        Icon = FaWordpress;
                        platformName = "Wordpress";
                        displayLabel = "Wordpress URL";
                      } else if (isType("behance")) {
                        Icon = FaBehance;
                        platformName = "Behance";
                        displayLabel = "Behance URL";
                      } else if (isType("tumblr")) {
                        Icon = FaTumblr;
                        platformName = "Tumblr";
                        displayLabel = "Tumblr URL";
                      }

                      const compositeField = `social_media:${idx}:${platformName}`;
                      const formattedHref = String(rawUrl)
                        .trim()
                        .startsWith("http")
                        ? rawUrl
                        : `https://${rawUrl}`;

                      return (
                        <div
                          key={idx}
                          className={`ai-contact-item ${vSelected(
                            isFieldActive(compositeField, [
                              url,
                              "social_media",
                              rawType,
                            ])
                          )} ${
                            isVisualEditMode &&
                            cardData.content_view_type === "iconDesign"
                              ? "visual-editable"
                              : ""
                          }`}
                          onClick={() => {
                            if (
                              isVisualEditMode &&
                              cardData.content_view_type === "iconDesign"
                            ) {
                              handlePreviewElementClick(
                                compositeField,
                                displayLabel
                              );
                            }
                          }}
                        >
                          <a
                            href={isVisualEditMode ? undefined : formattedHref}
                            target="_blank"
                            rel="noreferrer"
                            className="ai-icon-wrap"
                            style={{
                              backgroundColor: cardData.color || "#7000B5",
                            }}
                          >
                            <Icon size={14} color="#fff" />
                          </a>
                          <a
                            href={isVisualEditMode ? undefined : formattedHref}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => {
                              if (
                                isVisualEditMode &&
                                cardData.content_view_type !== "iconDesign"
                              ) {
                                e.preventDefault();
                                handlePreviewElementClick(
                                  compositeField,
                                  displayLabel
                                );
                              }
                            }}
                            className={`ai-line-text ${
                              isVisualEditMode &&
                              cardData.content_view_type !== "iconDesign"
                                ? "visual-editable"
                                : ""
                            }`}
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            {getPreviewValue(compositeField) || rawUrl || "Social Link"}
                          </a>
                        </div>
                      );
                    })
                  ) : (
                    <>
                      <div
                        className={`ai-contact-item ${vSelected(
                          isFieldActive("social_media")
                        )}`}
                      >
                        <div className="ai-dot"></div>
                        <div className="ai-line w80"></div>
                      </div>
                      <div className="ai-contact-item">
                        <div className="ai-dot"></div>
                        <div className="ai-line w80"></div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* END RIGHT */}
      </div>
    </div>
      <FeatureNotAvailableModal
        show={showLimitModal}
        handleClose={() => setShowLimitModal(false)}
        getData={() => fetchPreview(sessionUuid)}
      />
    </>
  );
};

export default AiCardChatPage;
